/* eslint-disable no-case-declarations */
import { useTranslation } from 'react-i18next';
import {
  CircularProgress,
  MenuItem,
  TextField,
  Popper,
  Paper,
  InputLabel,
  Stack,
  Button,
  useTheme,
  Typography,
} from '@mui/material';
import React, { useState, ChangeEvent, useCallback, useContext, useEffect, useRef } from 'react';
import { fetchAddresses } from '@/utils/location';
import debounce from 'debounce';
import i18n from '@/i18n/i18n';
import { ActivityContext } from '@/contexts/ActivityContext';
import { LocationContext } from '@/contexts/LocationContext';
import { Location } from '@/api/types/Location';

type LocationInputProps = {
  value: string;
  onChange: (value: string) => void;
  error: string | boolean;
  setError: (error: string | boolean) => void;
  isManagingLocations?: boolean;
};

const LocationInput = ({
  value,
  onChange,
  error,
  setError,
  isManagingLocations,
}: LocationInputProps) => {
  const { t } = useTranslation();
  const lang = i18n.language.split('-')[0];
  const { palette } = useTheme();
  const { activity, setActivity } = useContext(ActivityContext);
  const { locations } = useContext(LocationContext);
  const [suggestions, setSuggestions] = useState<(string | Location)[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [textFieldValue, setTextFieldValue] = useState<string>(value);
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState<number>(-1);

  const textFieldWrapperRef = useRef<HTMLDivElement | null>(null);
  const textFieldRef = useRef<HTMLInputElement | null>(null);
  const suggestionRefs = useRef<(HTMLLIElement | null)[]>([]);

  useEffect(() => {
    setTextFieldValue(value);
  }, [value]);

  useEffect(() => {
    suggestionRefs.current = suggestions.map(() => null);
  }, [suggestions]);

  const searchSavedLocations = (searchTerm: string): (string | Location)[] =>
    locations
      .filter(({ locationName }) => locationName?.toLowerCase().includes(searchTerm.toLowerCase()))
      .slice(0, 4);

  const debouncedFetch = useCallback(
    debounce(async (searchTerm: string) => {
      setLoading(true);
      setSuggestions([]);
      try {
        const results = await fetchAddresses(searchTerm, lang);
        if (isManagingLocations) {
          setSuggestions(results || []);
        } else {
          setSuggestions([...searchSavedLocations(searchTerm), ...(results || [])]);
        }
      } catch (error) {
        console.error('Error fetching addresses:', error);
      } finally {
        setLoading(false);
      }
    }, 300),
    [lang, locations],
  );

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    setTextFieldValue(newValue);

    if (isManagingLocations) {
      onChange(newValue);
    }

    if (!newValue) {
      setSuggestions([]);
      setError(false);
      return;
    }

    if (newValue.length < 3) return setSuggestions([]);
    if (newValue.length > 256) return setError('Address is too long');

    debouncedFetch(newValue);
  };

  const handleSelect = (suggestion: string | Location) => {
    if (typeof suggestion !== 'string') {
      setActivity({ ...activity, location: suggestion });
      const locationValue = suggestion.locationAddress || suggestion.locationName || '';
      setTextFieldValue(locationValue);
      onChange(locationValue);
    } else {
      setTextFieldValue(suggestion);
      onChange(suggestion);
    }

    setSuggestions([]);
    setAnchorEl(null);
    setActiveSuggestionIndex(-1);

    if (textFieldRef.current) {
      textFieldRef.current.focus();
    }
  };

  const handleBlur = (event: React.FocusEvent) => {
    if (!event.relatedTarget || !event.relatedTarget.closest('[role="listbox"]')) {
      setTimeout(() => {
        if (textFieldValue.length > 0) {
          onChange(textFieldValue);
        }
        setSuggestions([]);
        setAnchorEl(null);
        setActiveSuggestionIndex(-1);
      }, 100);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const isPopperOpen = suggestions.length > 0 && !!anchorEl;

    switch (event.key) {
      case 'ArrowDown':
        if (isPopperOpen) {
          event.preventDefault();
          setActiveSuggestionIndex((prevIndex) => {
            const newIndex = prevIndex < suggestions.length - 1 ? prevIndex + 1 : 0;
            if (suggestionRefs.current[newIndex]) {
              suggestionRefs.current[newIndex]?.focus();
            }
            return newIndex;
          });
        } else if (textFieldValue.length >= 3) {
          setAnchorEl(event.currentTarget as HTMLElement);
          debouncedFetch(textFieldValue);
        }
        break;

      case 'ArrowUp':
        if (isPopperOpen) {
          event.preventDefault();
          setActiveSuggestionIndex((prevIndex) => {
            const newIndex = prevIndex > 0 ? prevIndex - 1 : suggestions.length - 1;
            if (suggestionRefs.current[newIndex]) {
              suggestionRefs.current[newIndex]?.focus();
            }
            return newIndex;
          });
        }
        break;

      case 'Escape':
        if (isPopperOpen) {
          event.preventDefault();
          setSuggestions([]);
          setAnchorEl(null);
          setActiveSuggestionIndex(-1);
        }
        break;

      case 'Tab':
        if (isPopperOpen) {
          setSuggestions([]);
          setAnchorEl(null);
          setActiveSuggestionIndex(-1);
        }
        break;

      case 'Enter':
        if (isPopperOpen && activeSuggestionIndex >= 0) {
          event.preventDefault();
          handleSelect(suggestions[activeSuggestionIndex]);
        }
        break;
    }
  };

  const handleSuggestionKeyDown = (
    event: React.KeyboardEvent,
    index: number,
    suggestion: string | Location,
  ) => {
    switch (event.key) {
      case 'Enter':
      case 'Space':
        event.preventDefault();
        handleSelect(suggestion);
        break;

      case 'ArrowDown':
        event.preventDefault();
        const nextIndex = index < suggestions.length - 1 ? index + 1 : 0;
        setActiveSuggestionIndex(nextIndex);
        suggestionRefs.current[nextIndex]?.focus();
        break;

      case 'ArrowUp':
        event.preventDefault();
        const prevIndex = index > 0 ? index - 1 : suggestions.length - 1;
        setActiveSuggestionIndex(prevIndex);
        suggestionRefs.current[prevIndex]?.focus();
        break;

      case 'Escape':
        event.preventDefault();
        setSuggestions([]);
        setAnchorEl(null);
        setActiveSuggestionIndex(-1);
        if (textFieldRef.current) {
          textFieldRef.current.focus();
        }
        break;

      case 'Tab':
        setSuggestions([]);
        setAnchorEl(null);
        setActiveSuggestionIndex(-1);
        break;

      case 'Home':
        event.preventDefault();
        setActiveSuggestionIndex(0);
        suggestionRefs.current[0]?.focus();
        break;

      case 'End':
        event.preventDefault();
        const lastIndex = suggestions.length - 1;
        setActiveSuggestionIndex(lastIndex);
        suggestionRefs.current[lastIndex]?.focus();
        break;
    }
  };

  return (
    <>
      <Stack>
        <Stack direction="row" spacing={2}>
          <InputLabel htmlFor="location-input">
            {t(isManagingLocations ? 'Address' : 'Activity location')}
          </InputLabel>
        </Stack>
        <Stack ref={textFieldWrapperRef} direction="row" spacing={1.5} alignItems="center">
          <TextField
            fullWidth
            variant="outlined"
            placeholder={t('Enter a location') + '...'}
            value={textFieldValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            autoComplete="off"
            error={!!error}
            helperText={error || ''}
            onBlur={handleBlur}
            onFocus={(e) => setAnchorEl(e.currentTarget as HTMLElement)}
            id="location-input"
            inputRef={textFieldRef}
            inputProps={{
              'aria-autocomplete': 'list',
              'aria-controls': suggestions.length > 0 ? 'location-suggestions' : undefined,
              'aria-activedescendant':
                activeSuggestionIndex >= 0
                  ? `location-suggestion-${activeSuggestionIndex}`
                  : undefined,
            }}
            InputProps={{
              endAdornment: loading ? <CircularProgress size={20} /> : null,
            }}
            aria-describedby={error ? 'location-error-text' : undefined}
          />
        </Stack>
        {error && (
          <span id="location-error-text" className="sr-only">
            {error}
          </span>
        )}
      </Stack>
      <Popper
        open={suggestions.length > 0 && !!anchorEl}
        anchorEl={anchorEl}
        placement="bottom-start"
        sx={{
          zIndex: 1300,
          width: textFieldWrapperRef.current?.clientWidth || 'auto',
          maxWidth: '100%',
        }}
        role="presentation"
      >
        <Paper
          sx={{
            boxShadow: 4,
            width: '100%',
            maxHeight: 250,
            overflowY: 'auto',
          }}
          role="listbox"
          id="location-suggestions"
          aria-label={t('Available locations') as string}
        >
          {suggestions.map((suggestion, index) => {
            const isLocation = typeof suggestion !== 'string';
            const isActive = index === activeSuggestionIndex;
            const suggestionText = isLocation ? suggestion.locationName || '' : suggestion;

            return (
              <MenuItem
                key={index}
                ref={(el) => (suggestionRefs.current[index] = el)}
                onClick={() => handleSelect(suggestion)}
                onKeyDown={(e) => handleSuggestionKeyDown(e, index, suggestion)}
                role="option"
                id={`location-suggestion-${index}`}
                aria-selected={isActive}
                tabIndex={0}
                sx={{
                  py: 1.5,
                  borderBottom: `1px solid ${palette.grey[800]}`,
                  display: 'flex',
                  alignItems: 'center',
                  maxWidth: '100%',
                  ...(isLocation && {
                    borderLeft: `8px solid ${suggestion.color || 'grey'}`,
                  }),
                  ...(isActive && {
                    backgroundColor: palette.action.selected,
                  }),
                  '&:focus': {
                    backgroundColor: palette.action.selected,
                    outline: `2px solid ${palette.primary.main}`,
                  },
                }}
              >
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{ minWidth: 0, flex: 1, overflow: 'hidden' }}
                >
                  <Typography
                    sx={{
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      flex: 1,
                    }}
                  >
                    {suggestionText}
                  </Typography>
                  {isLocation && suggestion.locationAddress && (
                    <Typography
                      color={palette.grey[500]}
                      sx={{
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        flexShrink: 0,
                        maxWidth: '50%',
                      }}
                    >
                      {suggestion.locationAddress}
                    </Typography>
                  )}
                </Stack>
              </MenuItem>
            );
          })}
        </Paper>
      </Popper>
    </>
  );
};

export default LocationInput;
